import { useEffect, useState as classicUseState } from 'react';
import ReactDom from 'react-dom';

import { ReactComponent as SearchIcon } from '@assets/svg/table-search.svg';
import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactTooltip from 'react-tooltip';

import { StateMethods } from '@hookstate/core';
import { ITag, ITagLimitWithDesc } from '@models/Tag.model';
import { TagsLoading } from '@pages/ConfigurationPage/Components/TagsManagement/Components/loading/Tags.loading';
import { getTagsCategory } from '@services/queries/tagsCategory/getTagsCategory/getTagsCategory';
import { getTagFontColor } from '@utils';
import { format } from 'date-fns';
import { useQuery } from 'react-query';
import { Text } from '@components';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import {
  AddButton,
  ButtonText,
  Cancel,
  ChevronDiv,
  Close,
  CloseImg,
  Color,
  ColumnDiv,
  Confirm,
  Container,
  DeleteIcon,
  Divider,
  Form,
  Header,
  IconContainer,
  InputContainer,
  InputSearch,
  Item,
  Line,
  LineDivider,
  NoTags,
  Overlay,
  Row,
  RowDiv,
  SearchIconContainer,
  Spacing,
  Table,
  Tag,
  // Text,
  Title,
  Wrap,
} from './AssignTag.styles';
import { AddTagDialog } from './components/AddTagDialog/AddTagDialog';
import { Tags } from './components/Tags/Tags';

type PossibleTypes = ITagLimitWithDesc[] | ITag[];
interface IAssignTag {
  /**
   * Mount or unmount the dialog
   */
  open: StateMethods<boolean>;
  /**
   * A list of tags that have already been assigned
   */
  activeList?: Pick<PossibleTypes[number], 'id'>[];

  /**
   * Called when the confirm button is clicked.
   *
   * It accepts a list of newTags to assign (ITagLimitWithDesc[]), a done function to call when the "assigning" completes, and an err function to call when it fails.
   *
   * E.g Use:
   * ```tsx
   *
   *  <AssignTag
   *    onConfirm={(newTags, done,err)=>{
   *      mutationAddTagsToInventoryItem.mutate({newTags},{
   *        onSuccess(){
   *          // Do something
   *          done()
   *        } // If the call to the api passes
   *        onError(){
   *          // Do something about err
   *          err()
   *        }  // If the call to the api fails
   *       })
   *    }}
   *  />
   * ```
   * ## Note:
   *
   * The AssignTag dialog is independent of the tags query because it'll be used in mass edit where we don't use the queries until we save.
   * Because of this:
   *      1. Make sure to  refetch tags query after editing before calling "done"
   */
  onConfirm: (
    tags: ITagLimitWithDesc[],
    done: VoidFunction,
    err: VoidFunction,
  ) => void;
}

export const AssignTag = (props: IAssignTag) => {
  const { open, activeList = [], onConfirm } = props;
  const portalDiv = document.getElementById('portal');

  const [openCategory, setOpenCategory] = classicUseState<string[]>([]);
  const toggleOpen = (id: string) => {
    if (openCategory.find(item => item === id)) {
      setOpenCategory(openCategory.filter(item => item !== id));
    } else {
      setOpenCategory([...openCategory, id]);
    }
  };

  const [searchText, setSearchText] = classicUseState('');
  const [searchLocal, setSearchLocal] = classicUseState('');

  const [selectedTags, setSelectedTags] = classicUseState<ITagLimitWithDesc[]>(
    [],
  );
  const { data, isLoading } = useQuery(
    ['tagCategories', searchText],
    () =>
      getTagsCategory({
        search: searchText,
      }),
    {
      onSuccess: newData => {
        if (searchText) {
          setOpenCategory(newData.map(item => item.id));
        }
      },
    },
  );

  const [openAddTagDialog, setOpenAddTagDialog] =
    classicUseState<boolean>(false);

  // Submit States: idle, submitted, done. If done, trigger the close effect function.
  type ConfirmState = 'idle' | 'submitted' | 'done';
  const [confirmState, setConfirmState] = classicUseState<ConfirmState>('idle');

  function resetForm() {
    setSelectedTags([]);
    setSearchText('');
    setSearchLocal('');
    // reset state to show btn
    setConfirmState('idle');
  }
  useEffect(() => {
    if (confirmState === 'done') {
      // Close modal
      open.set(false);
      // Reset form
      resetForm();
    }
    if (confirmState === 'submitted') {
      const selectedClone = [...selectedTags];
      onConfirm(
        selectedClone,
        () => setConfirmState('done'),
        // Reset btn on err.
        () => setConfirmState('idle'),
      );
    }
  }, [confirmState]);
  const { isDesktop, isMobile } = useSelector(checkDevice());
  return open.value && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Assign Tag</Title>
              <Close
                onClick={() => {
                  open.set(false);
                  resetForm();
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            {/* <Divider height="24px" /> */}

            <Wrap>
              {selectedTags.length > 0 ? (
                selectedTags.map(tag => {
                  if (!tag) return null;
                  return (
                    <Tag
                      color={tag.tagCategory.color}
                      data-for="helpTooltip"
                      data-tip
                    >
                      {tag.name}
                      <DeleteIcon
                        onClick={() => {
                          setSelectedTags(
                            selectedTags.filter(item => item.id !== tag.id),
                          );
                        }}
                      >
                        <BlueIcon
                          icon="delete"
                          color={getTagFontColor(tag.tagCategory.color)}
                        />
                      </DeleteIcon>
                      <ReactTooltip
                        id="helpTooltip"
                        multiline
                        className="tooltip"
                        effect="solid"
                      >
                        Category: {tag.tagCategory.name}
                      </ReactTooltip>
                    </Tag>
                  );
                })
              ) : (
                <NoTags>No tags selected</NoTags>
              )}
            </Wrap>

            {/* <Divider height="24px" /> */}

            <Row>
              <InputContainer>
                <SearchIconContainer>
                  <SearchIcon />
                </SearchIconContainer>
                <InputSearch
                  placeholder="Find a tag"
                  value={searchLocal}
                  onChange={e => setSearchLocal(e.target.value)}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      setSearchText(searchLocal);
                      if (!searchLocal) {
                        setOpenCategory([]);
                      }
                    }
                  }}
                />
                {searchLocal !== '' && (
                  <CloseImg
                    onClick={() => {
                      if (setSearchLocal) setSearchLocal('');
                      setSearchText('');
                      setOpenCategory([]);
                    }}
                  />
                )}
              </InputContainer>

              {isDesktop && (
                <AddButton onClick={() => setOpenAddTagDialog(true)}>
                  <BlueIcon icon="plus" color="#8f95b2" />
                  <ButtonText>Create Tag</ButtonText>
                </AddButton>
              )}
              {isMobile && (
                <BActionIcon
                  variant="outline"
                  icon="plus"
                  size={42}
                  onClick={() => setOpenAddTagDialog(true)}
                />
              )}
            </Row>

            {/* <Divider height="16px" /> */}

            <Table>
              {data && !isLoading ? (
                data?.map(tagCategory => {
                  const isOpen = !!openCategory.find(
                    item => item === tagCategory.id,
                  );
                  return (
                    <Spacing key={tagCategory.id}>
                      <Row>
                        <Item onClick={() => toggleOpen(tagCategory.id)}>
                          <RowDiv>
                            <ChevronDiv>
                              {isOpen ? (
                                <IconContainer>
                                  <BlueIcon
                                    icon="chevron-up"
                                    size={16}
                                    color="#8F95B2"
                                  />
                                </IconContainer>
                              ) : (
                                <IconContainer>
                                  <BlueIcon
                                    icon="chevron-down"
                                    size={16}
                                    color="#8F95B2"
                                  />
                                </IconContainer>
                              )}
                            </ChevronDiv>

                            <Divider width="16px" />

                            <ColumnDiv>
                              <Row>
                                <Color color={tagCategory.color} />
                                <Text text="h400">{tagCategory.name}</Text>
                              </Row>
                              <Divider height="6px" />
                              <Text textColor="n700" text="p100">
                                {tagCategory.description}
                              </Text>
                            </ColumnDiv>
                          </RowDiv>

                          <Text text="p200" textColor="n800">
                            {tagCategory?.lastUsed &&
                              format(tagCategory.lastUsed, 'MM/dd/yyyy')}
                          </Text>
                        </Item>
                      </Row>
                      <LineDivider />
                      {isOpen && (
                        <>
                          <Tags
                            tagCategoryId={tagCategory.id}
                            searchText={searchText}
                            activeList={activeList}
                            selectedTags={selectedTags}
                            setSelectedTags={setSelectedTags}
                          />
                        </>
                      )}
                    </Spacing>
                  );
                })
              ) : (
                <TagsLoading />
              )}
            </Table>

            <Form>
              <Line>
                <Cancel
                  onClick={() => {
                    resetForm();
                    open.set(false);
                  }}
                >
                  Cancel
                </Cancel>
                <Confirm
                  type="button"
                  disabled={
                    confirmState === 'submitted' || selectedTags.length === 0
                  }
                  onClick={() => {
                    setConfirmState('submitted');
                  }}
                >
                  Confirm
                </Confirm>
              </Line>
            </Form>

            {openAddTagDialog && (
              <AddTagDialog
                open={openAddTagDialog}
                onClose={() => {
                  setOpenAddTagDialog(false);
                }}
              />
            )}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
